import {
  cleanSpecifiedTail,
  getTextContentFromInlineRange,
} from '@blocksuite/affine-rich-text';
import {
  DocModeProvider,
  TelemetryProvider,
  VirtualKeyboardProvider,
} from '@blocksuite/affine-shared/services';
import type { AffineInlineEditor } from '@blocksuite/affine-shared/types';
import { getViewportElement } from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ArrowLeftSmallIcon } from '@blocksuite/icons/lit';
import { html, LitElement, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import groupBy from 'lodash-es/groupBy';

import { AFFINE_SLASH_MENU_TRIGGER_KEY } from './consts.js';
import { mobileSlashMenuStyles } from './styles.js';
import type {
  SlashMenuActionItem,
  SlashMenuContext,
  SlashMenuItem,
} from './types.js';
import {
  filterSlashMenuItems,
  getSlashMenuActionContext,
  isSubMenuItem,
  parseGroup,
  slashItemClassName,
} from './utils.js';

export const AFFINE_MOBILE_SLASH_MENU = 'affine-mobile-slash-menu';

/**
 * The mobile counterpart of {@link SlashMenu}.
 *
 * A floating popover anchored to the caret does not work on a phone: the
 * virtual keyboard covers the lower half of the screen and there are no arrow
 * keys to drive selection. So this renders a sheet pinned directly above the
 * keyboard and driven entirely by taps, mirroring the shape of
 * `affine-mobile-linked-doc-menu`.
 *
 * Filtering is delegated to {@link filterSlashMenuItems}, the same function the
 * desktop popover uses, so argument commands like `/repeat every 3 days for 2
 * months` behave identically on both.
 */
export class MobileSlashMenu extends SignalWatcher(WithDisposable(LitElement)) {
  static override styles = mobileSlashMenuStyles;

  /**
   * Sub-menus are pushed onto a stack rather than opened as nested popovers;
   * a back row pops them.
   */
  @state()
  private accessor _stack: { title: string; items: SlashMenuItem[] }[] = [];

  @state()
  private accessor _filteredItems: SlashMenuItem[] = [];

  @state()
  private accessor _searching = false;

  private readonly _startRange = this.inlineEditor.getInlineRange();

  private get _query() {
    return getTextContentFromInlineRange(this.inlineEditor, this._startRange);
  }

  private get _telemetry() {
    return this.context.std.getOptional(TelemetryProvider);
  }

  get keyboard() {
    return this.context.std.get(VirtualKeyboardProvider);
  }

  /**
   * How far above the bottom of the viewport the sheet should sit.
   *
   * The mobile keyboard toolbar is `position: fixed; bottom: 0` and renders a
   * ~46px bar on top of a spacer the height of the keyboard, so its bar
   * occupies exactly the offset a naive `bottom: keyboardHeight` would use.
   * Anchoring to the toolbar's own top edge sits the sheet above it without
   * hard-coding the bar height.
   */
  private get _bottomOffset() {
    const toolbar = document.querySelector('affine-keyboard-toolbar');
    if (toolbar) {
      const { top } = toolbar.getBoundingClientRect();
      if (top > 0) return window.innerHeight - top;
    }
    return this.keyboard.height$.value;
  }

  constructor(
    private readonly inlineEditor: AffineInlineEditor,
    private readonly abortController = new AbortController()
  ) {
    super();
  }

  private readonly _updateFilteredItems = () => {
    const query = this._query;
    if (query === null) {
      this.abortController.abort();
      return;
    }

    const { items, state } = filterSlashMenuItems(
      this.items,
      query,
      this.context
    );
    this._filteredItems = items;
    this._searching = state !== 'off';
    // Typing narrows the top-level list, so any drilled-in sub-menu no longer
    // matches what the user is looking at.
    if (state !== 'off' && this._stack.length > 0) this._stack = [];
  };

  private readonly _handleActionItem = (item: SlashMenuActionItem) => {
    const query = this._query || '';
    const actionContext = getSlashMenuActionContext(item, query);

    // Same contract as desktop: tapping a picker item before the command name
    // is complete enters argument mode instead of firing the action.
    if (item.getArgumentItems && !actionContext) {
      this._completeQueryToCommand(item.name);
      return;
    }

    cleanSpecifiedTail(
      this.context.std,
      this.context.model,
      AFFINE_SLASH_MENU_TRIGGER_KEY + query
    );
    this.inlineEditor
      .waitForUpdate()
      .then(() => {
        item.action(this.context, actionContext);
        this._telemetry?.track('SelectSlashMenuItem', {
          page:
            this.context.std.get(DocModeProvider).getEditorMode() ?? undefined,
          segment:
            this.context.model.flavour === 'affine:edgeless-text'
              ? 'edgeless-text'
              : 'doc',
          module: 'slash menu',
          control: item.name,
        });
        this.abortController.abort();
      })
      .catch(console.error);
  };

  private readonly _completeQueryToCommand = (commandName: string) => {
    const startRange = this._startRange;
    if (!startRange) return;
    const cursorRange = this.inlineEditor.getInlineRange();
    if (!cursorRange) return;
    const startIdx = startRange.index;
    const endIdx = cursorRange.index;
    if (endIdx < startIdx) return;
    const replacement = commandName.toLowerCase() + ' ';
    if (endIdx > startIdx) {
      this.inlineEditor.deleteText({
        index: startIdx,
        length: endIdx - startIdx,
      });
    }
    this.inlineEditor.insertText({ index: startIdx, length: 0 }, replacement);
    this.inlineEditor.setInlineRange({
      index: startIdx + replacement.length,
      length: 0,
    });
    this.inlineEditor
      .waitForUpdate()
      .then(() => {
        this._updateFilteredItems();
      })
      .catch(console.error);
  };

  private readonly _focusEditor = () => {
    // A touch can blur the editable root before click, even when pointerdown
    // is cancelled. Restore it before changing the range or running an action.
    const range = this.inlineEditor.getInlineRange();
    this.inlineEditor.eventSource?.focus({ preventScroll: true });
    this.context.std.event.active = true;
    if (range) this.inlineEditor.setInlineRange(range);
  };

  private readonly _handleItem = (item: SlashMenuItem) => {
    this._focusEditor();
    if (isSubMenuItem(item)) {
      this._stack = [...this._stack, { title: item.name, items: item.subMenu }];
      return;
    }
    this._handleActionItem(item);
  };

  private readonly _scrollCaretIntoView = () => {
    const container = getViewportElement(this.context.std.host);
    const rootElement = this.inlineEditor.rootElement;
    if (!container || !rootElement) return;
    container.scrollTo({
      top:
        rootElement.getBoundingClientRect().top +
        container.scrollTop -
        // leave the line visible under the doc header
        46,
      behavior: 'smooth',
    });
  };

  private readonly _renderItem = (item: SlashMenuItem) => {
    const icon = item.icon
      ? html`<div class="icon">${item.icon}</div>`
      : nothing;
    const description = item.description
      ? html`<div class="description">${item.description}</div>`
      : nothing;

    return html`<button
      class="mobile-slash-menu-item ${slashItemClassName(item)}"
      data-testid=${item.name}
      @click=${(event: MouseEvent) => {
        // Closing on pointerup lets the synthesized click hit the page below.
        event.preventDefault();
        event.stopPropagation();
        this._handleItem(item);
      }}
    >
      ${icon}
      <div class="content">
        <div class="name">${item.name}</div>
        ${description}
      </div>
    </button>`;
  };

  private readonly _renderBackRow = (title: string) => {
    return html`<button
      class="mobile-slash-menu-item"
      data-testid="mobile-slash-menu-back"
      @click=${(event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this._focusEditor();
        this._stack = this._stack.slice(0, -1);
      }}
    >
      <div class="icon">${ArrowLeftSmallIcon()}</div>
      <div class="content"><div class="name">${title}</div></div>
    </button>`;
  };

  override connectedCallback() {
    super.connectedCallback();

    // Keep the editor focused so the virtual keyboard stays up while tapping.
    this._disposables.addFromEvent(this, 'pointerdown', e => {
      e.preventDefault();
    });

    this._disposables.addFromEvent(
      window,
      'pointerdown',
      e => {
        if (e.target === this || this.contains(e.target as Node)) return;
        this.abortController.abort();
      },
      true
    );

    const { eventSource } = this.inlineEditor;
    if (eventSource) {
      // `beforeinput` rather than `keydown`: on Android `event.key` is
      // usually `Unidentified`, so a keydown-driven query never updates.
      this._disposables.addFromEvent(eventSource, 'beforeinput', () => {
        this.inlineEditor
          .waitForUpdate()
          .then(() => {
            const curRange = this.inlineEditor.getInlineRange();
            if (
              this._startRange &&
              curRange &&
              curRange.index < this._startRange.index
            ) {
              // deleted past the `/`
              this.abortController.abort();
              return;
            }
            this._updateFilteredItems();
          })
          .catch(console.error);
      });
    }

    this._updateFilteredItems();
  }

  override firstUpdated() {
    this._scrollCaretIntoView();
  }

  override render() {
    // Read the signal so SignalWatcher re-renders when the keyboard moves.
    void this.keyboard.height$.value;
    this.style.bottom = `${this._bottomOffset}px`;

    const drilled = this._stack.at(-1);
    if (drilled) {
      return html`
        ${this._renderBackRow(drilled.title)}
        ${drilled.items.map(this._renderItem)}
      `;
    }

    if (this._searching) {
      if (this._filteredItems.length === 0) {
        return html`<div class="mobile-slash-menu-empty">No results</div>`;
      }
      return html`${this._filteredItems.map(this._renderItem)}`;
    }

    // Browsing the full list: keep the desktop's group headings.
    const groups = groupBy(this.items, item =>
      item.group ? parseGroup(item.group)[1] : 'Others'
    );
    return html`${Object.entries(groups).map(
      ([name, items]) => html`
        <div class="mobile-slash-menu-group-title">${name}</div>
        ${items.map(this._renderItem)}
      `
    )}`;
  }

  @property({ attribute: false })
  accessor context!: SlashMenuContext;

  @property({ attribute: false })
  accessor items!: SlashMenuItem[];
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_MOBILE_SLASH_MENU]: MobileSlashMenu;
  }
}
